/**
 * Hierarchy Data Service
 *
 * Loads WBS hierarchy, OBS, and cost burden profile data from mock JSON files.
 * For projects without pre-built mock data, auto-generates default hierarchies.
 * In-memory state supports adding/editing nodes within a session.
 * Drop-in replaceable with OData V4 when ready.
 */

import type {
  WbsHierarchy,
  HierarchyType,
  CostPlanningLevel,
  BaseHierarchyNode,
  SchedulingHierarchyNode,
  FinancialHierarchyNode,
  CostHierarchyNode,
  CostBurdenProfile,
  ObsHierarchy,
  ObsNode,
  BillingType,
  StandardWbsTemplate,
  CustomWbsHierarchy,
  CustomWbsNode,
  HierarchyNodeMapping,
  CrossHierarchyLink,
} from '@/types/hierarchy'
import * as scheduleService from '@/services/scheduleService'
import type { WbsNode, Activity } from '@/types/scheduling'
import { supabase } from '@/lib/supabase'

const MOCK_BASE = '/mockdata'
const HIERARCHY_DIR = 'hierarchy'
const fileCache = new Map<string, unknown>()

function isSupabaseMode(): boolean {
  return (import.meta.env.VITE_DATA_SOURCE as string) === 'supabase'
}

function getActiveDemoProfileDir(): string {
  try {
    const profileId = localStorage.getItem('ppm-demo-profile') ?? 'technology-digital'
    if (profileId === 'technology-digital' || profileId === 'default') return ''
    const profileDirs: Record<string, string> = {
      'engineering-construction': 'profiles/engineering-construction',
    }
    return profileDirs[profileId] ?? ''
  } catch { return '' }
}

/** Convert snake_case DB row to camelCase, merging JSONB `data` field if present */
function snakeToCamelRow(row: Record<string, unknown>): Record<string, unknown> {
  const { data: jsonData, created_at: _created_at, updated_at: _updated_at, ...cols } = row
  const result: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(cols)) {
    result[k.replace(/_([a-z])/g, (_, c) => c.toUpperCase())] = v
  }
  if (jsonData && typeof jsonData === 'object') {
    Object.assign(result, jsonData as Record<string, unknown>)
  }
  return result
}

/** Rows-per-request cap PostgREST enforces on these tables. */
const READ_PAGE = 1000

// Keyset ("seek") paging on the primary key.
//
// This used to be OFFSET/LIMIT via .range() with NO order at all. PostgREST
// leaves an unordered query in whatever order the planner returns, so under
// concurrent writes a row could come back on two pages or on none: a node's
// cost then double counted at its control account while another node vanished,
// and the hierarchy totals stopped tying. Paging on `id` (the text primary key,
// unique and NOT NULL on every table read here) gives a total order, and
// seeking past the last id seen means an insert mid-scan can no longer shift
// the window. Display order is applied afterwards by compareHierarchyNodes.
async function querySupabase<T>(table: string, filterCol?: string, filterVal?: string): Promise<T[]> {
  const all: Record<string, unknown>[] = []
  let cursor: string | null = null
  for (;;) {
    let q = supabase.from(table).select('*')
    if (filterCol && filterVal) q = q.eq(filterCol, filterVal)
    if (cursor !== null) q = q.gt('id', cursor)
    const { data, error } = await q.order('id', { ascending: true }).limit(READ_PAGE)
    // A partial read is NOT a usable read here: every consumer sums money off
    // these rows, so returning the pages we managed to fetch understates the
    // Project Total while the page still looks healthy. Fail loudly instead.
    if (error) throw new Error(`[hierarchy] read failed on ${table}: ${error.message}`)
    const rows = (data ?? []) as unknown as Record<string, unknown>[]
    all.push(...rows)
    if (rows.length < READ_PAGE) break
    cursor = String(rows[rows.length - 1]!.id)
  }
  return all.map(snakeToCamelRow) as T[]
}

// For tables stored as `id + data jsonb` (no flat columns) — filter by a JSONB key.
// jsonbKey is the camelCase path inside the `data` blob (e.g., "hierarchyId", "projectId").
// Same keyset paging and same fail-loud contract as querySupabase above.
async function querySupabaseJsonb<T>(table: string, jsonbKey: string, filterVal: string): Promise<T[]> {
  const all: Record<string, unknown>[] = []
  let cursor: string | null = null
  for (;;) {
    let q = supabase
      .from(table)
      .select('*')
      .filter(`data->>${jsonbKey}`, 'eq', filterVal)
    if (cursor !== null) q = q.gt('id', cursor)
    const { data, error } = await q.order('id', { ascending: true }).limit(READ_PAGE)
    if (error) throw new Error(`[hierarchy] read failed on ${table}: ${error.message}`)
    const rows = (data ?? []) as unknown as Record<string, unknown>[]
    all.push(...rows)
    if (rows.length < READ_PAGE) break
    cursor = String(rows[rows.length - 1]!.id)
  }
  return all.map(snakeToCamelRow) as T[]
}

// --- Canonical hierarchy ordering -------------------------------------------

/** The minimum a node needs for the canonical sibling order. */
export interface OrderableNode {
  id: string
  sortOrder?: number | null
  code?: string | null
}

/**
 * Canonical order for siblings anywhere in the app: explicit `sortOrder` first,
 * then `code` in natural order, then `id`.
 *
 * Why the tie-breakers matter: `cost_nodes.sort_order` defaults to 0, so a whole
 * hierarchy routinely arrives with every row on the same value. Sorting on
 * sortOrder alone then leaves the render order equal to the arrival order from
 * an unordered paged read, which is why the same tree drew differently on every
 * reload. Falling through to code and then to the primary key makes the order
 * TOTAL, so the tree is identical every time whatever order the rows arrive in.
 *
 * Nodes with no sortOrder sort LAST within their group rather than jumping to
 * the top, matching "nulls last" on the database side.
 */
export function compareHierarchyNodes(a: OrderableNode, b: OrderableNode): number {
  const ao = a.sortOrder
  const bo = b.sortOrder
  const aHas = typeof ao === 'number' && Number.isFinite(ao)
  const bHas = typeof bo === 'number' && Number.isFinite(bo)
  if (aHas && bHas && ao !== bo) return ao! < bo! ? -1 : 1
  if (aHas !== bHas) return aHas ? -1 : 1
  // Natural code compare so 1.10 follows 1.9 instead of preceding it.
  const byCode = String(a.code ?? '').localeCompare(String(b.code ?? ''), undefined, { numeric: true })
  if (byCode !== 0) return byCode
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

/** Copy of `nodes` in canonical order. Never mutates the input. */
export function sortHierarchyNodes<T extends OrderableNode>(nodes: T[]): T[] {
  return [...nodes].sort(compareHierarchyNodes)
}

/**
 * Work out the new sortOrder values that move `nodeId` one position up or down
 * among its siblings. Returns the FULL sibling group renumbered, or an empty
 * array when the node is already at the end it is moving toward.
 *
 * The group is renumbered rather than swapping two values because ties are the
 * normal case (the column default is 0), and with ties a swap moves nothing.
 */
export function computeSiblingReorder<T extends OrderableNode & { parentNodeId: string | null }>(
  nodes: T[],
  nodeId: string,
  direction: 'up' | 'down',
): Array<{ id: string; sortOrder: number }> {
  const node = nodes.find(n => n.id === nodeId)
  if (!node) return []
  const parentKey = node.parentNodeId ?? null
  const siblings = sortHierarchyNodes(nodes.filter(n => (n.parentNodeId ?? null) === parentKey))
  const idx = siblings.findIndex(n => n.id === nodeId)
  const swapWith = direction === 'up' ? idx - 1 : idx + 1
  if (idx < 0 || swapWith < 0 || swapWith >= siblings.length) return []
  const reordered = [...siblings]
  reordered[idx] = siblings[swapWith]!
  reordered[swapWith] = siblings[idx]!
  // Step by 10 so a later drag-and-drop still has room to land between two rows.
  return reordered.map((n, i) => ({ id: n.id, sortOrder: (i + 1) * 10 }))
}

async function load<T>(file: string): Promise<T> {
  const profileDir = getActiveDemoProfileDir()
  const hierarchyFile = `${HIERARCHY_DIR}/${file}`
  const resolvedFile = profileDir ? `${profileDir}/${hierarchyFile}` : hierarchyFile
  const cacheKey = resolvedFile

  const cached = fileCache.get(cacheKey)
  if (cached) return cached as T

  // Try profile-specific file first, fall back to default
  if (profileDir) {
    const profileRes = await fetch(`${MOCK_BASE}/${resolvedFile}`)
    if (profileRes.ok) {
      const data = await profileRes.json()
      fileCache.set(cacheKey, data)
      return data as T
    }
    // Fallback to default
    const fallbackRes = await fetch(`${MOCK_BASE}/${hierarchyFile}`)
    if (!fallbackRes.ok) throw new Error(`Failed to load hierarchy data: ${file}`)
    const data = await fallbackRes.json()
    fileCache.set(cacheKey, data)
    return data as T
  }

  const res = await fetch(`${MOCK_BASE}/${hierarchyFile}`)
  if (!res.ok) throw new Error(`Failed to load hierarchy data: ${file}`)
  const data = await res.json()
  fileCache.set(cacheKey, data)
  return data as T
}

// --- In-memory session state (supports add/edit within session) ---

const sessionHierarchies = new Map<string, WbsHierarchy[]>()
const sessionSchedulingNodes = new Map<string, SchedulingHierarchyNode[]>()
const sessionFinancialNodes = new Map<string, FinancialHierarchyNode[]>()
const sessionCostNodes = new Map<string, CostHierarchyNode[]>()
const sessionObsNodes = new Map<string, ObsNode[]>()

/**
 * Mint a node id.
 *
 * This was `Date.now()` seeded then incremented, so two sessions opened in the
 * same millisecond minted the identical sequence. Every writer here upserts on
 * `id`, which meant one session's node silently REPLACED the other's. The
 * prefix is kept because the UI and the demo seed data read it; only the
 * uniqueness source changed.
 */
function randomIdSuffix(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c?.randomUUID) return c.randomUUID()
  if (c?.getRandomValues) {
    const buf = new Uint8Array(16)
    c.getRandomValues(buf)
    return Array.from(buf, b => b.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`
}

function nextId(prefix: string) {
  return `${prefix}_${randomIdSuffix()}`
}

export function clearHierarchyCache() {
  fileCache.clear()
}

/**
 * Drop the session copy of a scheduling hierarchy's nodes so the next read
 * goes back to the store (or rebuilds from the schedule). Called when the
 * Scheduling page adds a WBS element outside this service.
 */
export function invalidateSchedulingNodesCache(hierarchyId: string) {
  sessionSchedulingNodes.delete(hierarchyId)
}

// --- Generate default hierarchies for any project ---
// Each hierarchy starts with a root node that is 1:1 with the project definition.

function generateDefaultHierarchies(projectId: string, projectName: string, projectCode?: string): WbsHierarchy[] {
  const now = new Date().toISOString()
  const code = projectCode || '1.0'

  const schedHierId = `hier_sched_${projectId}`
  const finHierId = `hier_fin_${projectId}`
  const costHierId = `hier_cost_${projectId}`

  // NOTE: Scheduling nodes are NOT seeded here — they are pulled from
  // the schedule service in getSchedulingNodes() so they stay in sync
  // with the Integrated Master Schedule.

  // Seed root financial node
  sessionFinancialNodes.set(finHierId, [{
    id: `fn_root_${projectId}`,
    hierarchyId: finHierId,
    parentNodeId: null,
    code,
    name: projectName,
    description: `Project definition element for ${projectName}`,
    level: 0,
    sortOrder: 1,
    nodeType: 'Summary',
    revRecTypeId: null,
    revenueRecognitionMethod: null,
    financialMapping: null,
    performanceObligationId: null,
    revRecId: null,
    contractId: null,
    billingType: null,
    periodOfPerformanceStart: null,
    periodOfPerformanceEnd: null,
    customerName: null,
    customerId: null,
    deliverableIds: [],
    costCollectorCode: null,
    allowActuals: false,
    budgetTotal: 0,
    budgetSpent: 0,
    budgetCommitted: 0,
    eacCost: null,
    eacBottomsUp: null,
    eacRiskAdjustment: null,
    eacFinanceAdjustment: null,
    eacVariableConsideration: null,
  }])

  // Seed root cost node
  sessionCostNodes.set(costHierId, [{
    id: `cn_root_${projectId}`,
    hierarchyId: costHierId,
    parentNodeId: null,
    code,
    name: projectName,
    description: `Project definition element for ${projectName}`,
    level: 0,
    sortOrder: 1,
    nodeType: 'Summary',
    obsNodeId: null,
    obsNodeName: null,
    camUserId: null,
    camUserName: null,
    deliverables: [],
    costBurdenProfileId: null,
    costBurdenProfileName: null,
    plannedValue: 0,
    earnedValue: 0,
    actualCost: 0,
    budgetAtCompletion: 0,
    estimateAtCompletion: 0,
    plannedHours: 0,
    actualHours: 0,
    remainingHours: 0,
    canCollectActuals: false,
    financialCostCollectorId: null,
    laborCategories: [],
  }])

  return [
    {
      id: schedHierId,
      projectId,
      name: `${projectName} - Execution WBS`,
      type: 'scheduling' as HierarchyType,
      status: 'Active',
      description: 'Scheduling hierarchy synced to the Integrated Master Schedule',
      isDefault: true,
      isSyncedToSchedule: true,
      createdDate: now,
      createdBy: 'user_pm1',
      lastModifiedDate: now,
      lastModifiedBy: 'user_pm1',
      nodeCount: 1,
    },
    {
      id: finHierId,
      projectId,
      name: `${projectName} - Financial WBS`,
      type: 'financial' as HierarchyType,
      status: 'Active',
      description: 'Financial hierarchy for revenue recognition, billing, and cost collection',
      isDefault: true,
      isSyncedToSchedule: false,
      createdDate: now,
      createdBy: 'user_fin1',
      lastModifiedDate: now,
      lastModifiedBy: 'user_fin1',
      nodeCount: 1,
    },
    {
      id: costHierId,
      projectId,
      name: `${projectName} - Cost Management WBS`,
      type: 'cost' as HierarchyType,
      status: 'Active',
      description: 'Cost management hierarchy for resource planning, control accounts, and earned value',
      isDefault: true,
      isSyncedToSchedule: false,
      createdDate: now,
      createdBy: 'user_pm1',
      lastModifiedDate: now,
      lastModifiedBy: 'user_pm1',
      nodeCount: 1,
      // Default the Cost Management WBS to detailed Work Package planning.
      planningLevel: 'WorkPackage',
    },
  ]
}

/**
 * Update the Cost Management WBS planning level (Control Account vs Work
 * Package) for a project's cost hierarchy. Mutates the in-session hierarchy
 * list so the change is visible immediately and, in Supabase mode, persists
 * `planning_level` on the wbs_hierarchies row so it survives a reload.
 */
export function updateHierarchyPlanningLevel(
  projectId: string,
  hierarchyId: string,
  planningLevel: CostPlanningLevel,
): void {
  const hierarchies = sessionHierarchies.get(projectId)
  if (hierarchies) {
    const now = new Date().toISOString()
    sessionHierarchies.set(
      projectId,
      hierarchies.map(h =>
        h.id === hierarchyId ? { ...h, planningLevel, lastModifiedDate: now } : h,
      ),
    )
  }
  if (isSupabaseMode()) {
    void supabase
      .from('wbs_hierarchies')
      .update({ planning_level: planningLevel, last_modified_date: new Date().toISOString() })
      .eq('id', hierarchyId)
      .then(({ error }) => {
        if (error) console.warn(`[hierarchy] Failed to persist planning_level for ${hierarchyId}:`, error.message)
      })
  }
}

// --- WBS Hierarchies ---

export async function getWbsHierarchies(projectId: string, projectName?: string, projectCode?: string): Promise<WbsHierarchy[]> {
  // Check session state first
  if (sessionHierarchies.has(projectId)) {
    return sessionHierarchies.get(projectId)!
  }

  // Try Supabase first
  if (isSupabaseMode()) {
    const rows = await querySupabase<WbsHierarchy>('wbs_hierarchies', 'project_id', projectId)
    if (rows.length > 0) {
      // Persisted rows may cover only SOME hierarchy types — e.g. the app
      // persists a single generated `cost` row while editing. Without merging,
      // the presence of that one row makes this function return ONLY persisted
      // rows and stop generating the others, so the financial/scheduling WBS
      // (which are produced on the fly) silently disappear from the UI even
      // though their nodes still exist. Generate the standard set and append
      // the types that aren't already persisted so one type never hides the
      // rest. Node reads are Supabase-first, so generateDefaultHierarchies'
      // session root-seeding is harmless here.
      const presentTypes = new Set(rows.map((r) => r.type))
      const generated = generateDefaultHierarchies(projectId, projectName || projectId, projectCode)
      const merged = [...rows, ...generated.filter((g) => !presentTypes.has(g.type))]
      sessionHierarchies.set(projectId, merged)
      return merged
    }
  }

  // Try loading from mock data
  try {
    const data = await load<{ wbsHierarchies: WbsHierarchy[] }>('wbs-hierarchies.json')
    const matches = data.wbsHierarchies.filter(h => h.projectId === projectId)
    if (matches.length > 0) {
      sessionHierarchies.set(projectId, matches)
      return matches
    }
  } catch {
    // Ignore load failures
  }

  // Auto-generate for projects without pre-built data
  const generated = generateDefaultHierarchies(projectId, projectName || projectId, projectCode)
  sessionHierarchies.set(projectId, generated)
  return generated
}

// --- Convert schedule WBS nodes to scheduling hierarchy nodes ---

interface WbsDateRollup {
  earlyStart: string | null
  earlyFinish: string | null
  lateStart: string | null
  lateFinish: string | null
  actualStart: string | null
  actualFinish: string | null
  baselineStart: string | null
  baselineFinish: string | null
  percentComplete: number | null
  totalFloat: number | null
  activityCount: number
}

function minDate(...dates: (string | null | undefined)[]): string | null {
  const valid = dates.filter((d): d is string => !!d)
  if (valid.length === 0) return null
  return valid.sort()[0] ?? null
}

function maxDate(...dates: (string | null | undefined)[]): string | null {
  const valid = dates.filter((d): d is string => !!d)
  if (valid.length === 0) return null
  return valid.sort().reverse()[0] ?? null
}

/** Exported for the rollup regression tests; not part of the page-facing API. */
export function rollupDatesForWbs(wbsId: string, activities: Activity[]): WbsDateRollup {
  const wbsActivities = activities.filter(a => a.wbsId === wbsId)
  if (wbsActivities.length === 0) {
    return {
      earlyStart: null, earlyFinish: null,
      lateStart: null, lateFinish: null,
      actualStart: null, actualFinish: null,
      baselineStart: null, baselineFinish: null,
      percentComplete: null, totalFloat: null,
      activityCount: 0,
    }
  }

  // Weight percent complete by duration. A straight average over the activity
  // COUNT let one 500-day activity at 0% sit beside nine 1-day activities at
  // 100% and report the WBS as 90% done. Duration is the weight the schedule
  // already carries; when every activity is zero-duration there is nothing to
  // weight by, so fall back to the plain mean.
  const totalDuration = wbsActivities.reduce((sum, a) => sum + Math.max(0, a.originalDuration || 0), 0)
  const percentComplete = totalDuration > 0
    ? Math.round(
        wbsActivities.reduce((sum, a) => sum + (a.percentComplete || 0) * Math.max(0, a.originalDuration || 0), 0) /
        totalDuration,
      )
    : Math.round(wbsActivities.reduce((sum, a) => sum + (a.percentComplete || 0), 0) / wbsActivities.length)

  // Total float. The old form was Math.min(...list.filter(f => f !== 999)),
  // which returned Infinity for an empty list (CPM has never run) and also
  // threw away a genuine 999-day float. Filter on "has a value" instead and
  // report null when nothing does.
  const floats = wbsActivities
    .map(a => a.totalFloat)
    .filter((f): f is number => f != null && Number.isFinite(f))

  return {
    earlyStart: minDate(...wbsActivities.map(a => a.earlyStart)),
    earlyFinish: maxDate(...wbsActivities.map(a => a.earlyFinish)),
    lateStart: minDate(...wbsActivities.map(a => a.lateStart)),
    lateFinish: maxDate(...wbsActivities.map(a => a.lateFinish)),
    actualStart: minDate(...wbsActivities.map(a => a.actualStart)),
    actualFinish: wbsActivities.every(a => a.actualFinish) ? maxDate(...wbsActivities.map(a => a.actualFinish)) : null,
    baselineStart: minDate(...wbsActivities.map(a => a.baselineStart)),
    baselineFinish: maxDate(...wbsActivities.map(a => a.baselineFinish)),
    percentComplete,
    totalFloat: floats.length > 0 ? Math.min(...floats) : null,
    activityCount: wbsActivities.length,
  }
}

function scheduleWbsToHierarchyNode(wbs: WbsNode, hierarchyId: string, activities: Activity[], allWbs: WbsNode[]): SchedulingHierarchyNode {
  const dates = rollupDatesForWbs(wbs.id, activities)
  // In the scheduling WBS, nodes are either Summary (have children) or WbsElement (leaf).
  // CA/WP/PP concepts belong to the Cost Management hierarchy, not scheduling.
  void allWbs  // hasChildren detection moved to upstream consumers
  return {
    id: `sh_${wbs.id}`,
    hierarchyId,
    parentNodeId: wbs.parentId ? `sh_${wbs.parentId}` : null,
    code: wbs.code,
    name: wbs.name,
    description: wbs.description || '',
    level: wbs.level,
    sortOrder: wbs.sortOrder,
    elementType: 'WbsElement',
    schedulingWbsNodeId: wbs.id,
    responsibleOrg: wbs.responsibleOrg || null,
    budgetAtCompletion: wbs.budgetAtCompletion || null,
    earlyStart: dates.earlyStart,
    earlyFinish: dates.earlyFinish,
    lateStart: dates.lateStart,
    lateFinish: dates.lateFinish,
    actualStart: dates.actualStart,
    actualFinish: dates.actualFinish,
    baselineStart: dates.baselineStart,
    baselineFinish: dates.baselineFinish,
    percentComplete: dates.percentComplete,
    // rollupDatesForWbs already returns null when no activity has a float;
    // 999 is a real float value, not a sentinel, so it passes through.
    totalFloat: dates.totalFloat,
    activityCount: dates.activityCount,
  }
}

// --- Scheduling Nodes ---

/** projectId is used to pull schedule WBS (always preferred for scheduling hierarchies) */
/**
 * Read the scheduling nodes for a hierarchy in canonical sibling order.
 * Ordering is applied on the way out of the service so every consumer (tree,
 * table, export, AI panel) sees the same order without repeating the rule.
 */
export async function getSchedulingNodes(hierarchyId: string, projectId?: string): Promise<SchedulingHierarchyNode[]> {
  return sortHierarchyNodes(await loadSchedulingNodes(hierarchyId, projectId))
}

async function loadSchedulingNodes(hierarchyId: string, projectId?: string): Promise<SchedulingHierarchyNode[]> {
  // In Supabase mode, always query the database first so a stale session seed
  // doesn't shadow persisted nodes.
  if (isSupabaseMode()) {
    const rows = await querySupabaseJsonb<SchedulingHierarchyNode>('scheduling_nodes', 'hierarchyId', hierarchyId)
    if (rows.length > 0) {
      sessionSchedulingNodes.set(hierarchyId, rows)
      return rows
    }
  }

  if (sessionSchedulingNodes.has(hierarchyId)) {
    return sessionSchedulingNodes.get(hierarchyId)!
  }

  // Primary: pull WBS from schedule data (the same source the Scheduling page uses)
  if (projectId) {
    try {
      const scheduleData = await scheduleService.getScheduleDataByProject(projectId)
      if (scheduleData.wbs && scheduleData.wbs.length > 0) {
        const activities = scheduleData.activities || []
        const nodes = scheduleData.wbs.map(w => scheduleWbsToHierarchyNode(w, hierarchyId, activities, scheduleData.wbs))
        sessionSchedulingNodes.set(hierarchyId, nodes)
        return nodes
      }
    } catch {
      // No schedule data available
    }
  }

  // Fallback: try pre-built hierarchy mock data
  try {
    const data = await load<{ schedulingNodes: SchedulingHierarchyNode[] }>('scheduling-nodes.json')
    const matches = data.schedulingNodes.filter(n => n.hierarchyId === hierarchyId)
    if (matches.length > 0) {
      sessionSchedulingNodes.set(hierarchyId, matches)
      return matches
    }
  } catch {
    // Ignore
  }

  // No schedule or mock data — seed a root node as project definition element
  const rootNode: SchedulingHierarchyNode = {
    id: `sn_root_${hierarchyId}`,
    hierarchyId,
    parentNodeId: null,
    code: '1.0',
    name: hierarchyId.replace('hier_sched_', ''),
    description: 'Project definition element',
    level: 0,
    sortOrder: 1,
    elementType: 'WbsElement',
    schedulingWbsNodeId: null,
    responsibleOrg: null,
    budgetAtCompletion: null,
    earlyStart: null, earlyFinish: null,
    lateStart: null, lateFinish: null,
    actualStart: null, actualFinish: null,
    baselineStart: null, baselineFinish: null,
    percentComplete: null, totalFloat: null, activityCount: null,
  }
  sessionSchedulingNodes.set(hierarchyId, [rootNode])
  return [rootNode]
}

/**
 * Bulk-insert/upsert scheduling nodes — the path the CSV/Excel importer
 * uses for multi-thousand-row WBS uploads. Splits into ~500-row chunks
 * to stay under PostgREST's request-size limits and to keep the round
 * trip responsive on slow connections.
 */
export async function bulkUpsertSchedulingNodes(
  hierarchyId: string,
  nodes: SchedulingHierarchyNode[],
  options?: { replaceHierarchy?: boolean; chunkSize?: number }
): Promise<{ inserted: number; failed: number; errors: string[] }> {
  const chunkSize = options?.chunkSize ?? 500
  const errors: string[] = []
  let inserted = 0

  // Update the in-memory session cache up-front so the UI reflects the
  // import immediately even if Supabase persistence is still in flight.
  const existing = sessionSchedulingNodes.get(hierarchyId) || []
  const merged = options?.replaceHierarchy ? nodes : (() => {
    const byId = new Map(existing.map((n) => [n.id, n]))
    for (const n of nodes) byId.set(n.id, n)
    return Array.from(byId.values())
  })()
  sessionSchedulingNodes.set(hierarchyId, merged)

  if (!isSupabaseMode()) {
    return { inserted: nodes.length, failed: 0, errors: [] }
  }

  if (options?.replaceHierarchy) {
    // Wipe rows belonging to this hierarchy so we don't leave orphans
    // when the upload represents the full structure.
    const { error: delErr } = await supabase
      .from('scheduling_nodes')
      .delete()
      .filter('data->>hierarchyId', 'eq', hierarchyId)
    // Abort rather than upsert on top of rows we failed to clear: that leaves
    // the old structure and the new one interleaved in one hierarchy.
    if (delErr) {
      sessionSchedulingNodes.delete(hierarchyId)
      return { inserted: 0, failed: nodes.length, errors: [`Failed to clear existing nodes: ${delErr.message}`] }
    }
  }

  // Chunked upsert. Each row is { id, data: <full node JSON> }.
  for (let i = 0; i < nodes.length; i += chunkSize) {
    const slice = nodes.slice(i, i + chunkSize)
    const payload = slice.map((n) => ({ id: n.id, data: n }))
    const { error } = await supabase.from('scheduling_nodes').upsert(payload)
    if (error) {
      // Stop on the first failed chunk. There is no transaction spanning the
      // chunks, so carrying on leaves every descendant of the failed slice
      // orphaned under a parent that never landed, and their cost drops out of
      // the rollup with nothing on screen to say so. Drop the optimistic
      // session cache too so the next read sees the database, not this claim.
      errors.push(`Chunk ${Math.floor(i / chunkSize) + 1}: ${error.message}`)
      sessionSchedulingNodes.delete(hierarchyId)
      return { inserted, failed: nodes.length - inserted, errors }
    }
    inserted += slice.length
  }

  return { inserted, failed: 0, errors }
}

/**
 * Bulk-insert/upsert financial nodes. financial_nodes is the JSONB-shaped
 * table (id text PK + data jsonb), same persistence pattern as
 * scheduling_nodes. We filter on `data->>hierarchyId` so the
 * replaceHierarchy path can clear the right slice.
 */
export async function bulkUpsertFinancialNodes(
  hierarchyId: string,
  nodes: FinancialHierarchyNode[],
  options?: { replaceHierarchy?: boolean; chunkSize?: number }
): Promise<{ inserted: number; failed: number; errors: string[] }> {
  const chunkSize = options?.chunkSize ?? 500
  const errors: string[] = []
  let inserted = 0

  const existing = sessionFinancialNodes.get(hierarchyId) || []
  const merged = options?.replaceHierarchy ? nodes : (() => {
    const byId = new Map(existing.map((n) => [n.id, n]))
    for (const n of nodes) byId.set(n.id, n)
    return Array.from(byId.values())
  })()
  sessionFinancialNodes.set(hierarchyId, merged)

  if (!isSupabaseMode()) return { inserted: nodes.length, failed: 0, errors: [] }

  if (options?.replaceHierarchy) {
    const { error: delErr } = await supabase
      .from('financial_nodes')
      .delete()
      .filter('data->>hierarchyId', 'eq', hierarchyId)
    if (delErr) {
      sessionFinancialNodes.delete(hierarchyId)
      return { inserted: 0, failed: nodes.length, errors: [`Failed to clear existing nodes: ${delErr.message}`] }
    }
  }

  for (let i = 0; i < nodes.length; i += chunkSize) {
    const slice = nodes.slice(i, i + chunkSize)
    const payload = slice.map((n) => ({ id: n.id, data: n }))
    const { error } = await supabase.from('financial_nodes').upsert(payload)
    if (error) {
      // See bulkUpsertSchedulingNodes: abort on the first failed chunk so a
      // half-written hierarchy is reported, not silently accepted.
      errors.push(`Chunk ${Math.floor(i / chunkSize) + 1}: ${error.message}`)
      sessionFinancialNodes.delete(hierarchyId)
      return { inserted, failed: nodes.length - inserted, errors }
    }
    inserted += slice.length
  }

  return { inserted, failed: 0, errors }
}

/**
 * Bulk-insert/upsert cost nodes. Unlike scheduling/financial, cost_nodes
 * is a structured table (snake_case columns), so we route every node
 * through costNodeToRow before upserting. replaceHierarchy filters on
 * the hierarchy_id column (also snake_case).
 */
export async function bulkUpsertCostNodes(
  hierarchyId: string,
  nodes: CostHierarchyNode[],
  options?: { replaceHierarchy?: boolean; chunkSize?: number }
): Promise<{ inserted: number; failed: number; errors: string[] }> {
  const chunkSize = options?.chunkSize ?? 500
  const errors: string[] = []
  let inserted = 0

  const existing = sessionCostNodes.get(hierarchyId) || []
  const merged = options?.replaceHierarchy ? nodes : (() => {
    const byId = new Map(existing.map((n) => [n.id, n]))
    for (const n of nodes) byId.set(n.id, n)
    return Array.from(byId.values())
  })()
  sessionCostNodes.set(hierarchyId, merged)

  if (!isSupabaseMode()) return { inserted: nodes.length, failed: 0, errors: [] }

  if (options?.replaceHierarchy) {
    const { error: delErr } = await supabase
      .from('cost_nodes')
      .delete()
      .eq('hierarchy_id', hierarchyId)
    if (delErr) {
      sessionCostNodes.delete(hierarchyId)
      return { inserted: 0, failed: nodes.length, errors: [`Failed to clear existing nodes: ${delErr.message}`] }
    }
  }

  for (let i = 0; i < nodes.length; i += chunkSize) {
    const slice = nodes.slice(i, i + chunkSize)
    const payload = slice.map((n) => costNodeToRow(n))
    const { error } = await supabase.from('cost_nodes').upsert(payload)
    if (error) {
      // See bulkUpsertSchedulingNodes: a transient failure on chunk 2 used to
      // orphan every descendant in the later chunks and drop their cost.
      errors.push(`Chunk ${Math.floor(i / chunkSize) + 1}: ${error.message}`)
      sessionCostNodes.delete(hierarchyId)
      return { inserted, failed: nodes.length - inserted, errors }
    }
    inserted += slice.length
  }

  return { inserted, failed: 0, errors }
}

export function addSchedulingNode(node: SchedulingHierarchyNode): SchedulingHierarchyNode {
  const nodes = sessionSchedulingNodes.get(node.hierarchyId) || []
  sessionSchedulingNodes.set(node.hierarchyId, [...nodes, node])
  if (isSupabaseMode()) {
    void supabase.from('scheduling_nodes').upsert({ id: node.id, data: node }).then(({ error }) => {
      if (error) console.warn(`[hierarchy] Failed to persist scheduling_node ${node.id}:`, error.message)
    })
  }
  return node
}

export async function updateSchedulingNode(hierarchyId: string, nodeId: string, updates: Partial<SchedulingHierarchyNode>): Promise<void> {
  const nodes = sessionSchedulingNodes.get(hierarchyId)
  if (!nodes) return
  const merged = nodes.map(n => n.id === nodeId ? { ...n, ...updates } : n)
  sessionSchedulingNodes.set(hierarchyId, merged)
  if (isSupabaseMode()) {
    const updated = merged.find(n => n.id === nodeId)
    if (updated) {
      const { error } = await supabase.from('scheduling_nodes').upsert({ id: nodeId, data: updated })
      if (error) console.warn(`[hierarchy] Failed to persist scheduling_node update ${nodeId}:`, error.message)
    }
  }
}

/**
 * Collect a node's id together with every descendant id under it. Used by
 * the delete*Node functions to cascade the removal so deleting a parent
 * doesn't leave its children orphaned in the table with a dangling
 * parentNodeId.
 *
 * Iterative BFS over the parentNodeId edges; safe against cycles (each
 * id is added to the visited set exactly once).
 */
function collectSubtreeIds<T extends { id: string; parentNodeId: string | null }>(
  nodes: T[],
  rootId: string,
): string[] {
  const childrenByParent = new Map<string, string[]>()
  for (const n of nodes) {
    if (!n.parentNodeId) continue
    const bucket = childrenByParent.get(n.parentNodeId)
    if (bucket) bucket.push(n.id)
    else childrenByParent.set(n.parentNodeId, [n.id])
  }
  const ids: string[] = [rootId]
  const queue: string[] = [rootId]
  const seen = new Set<string>([rootId])
  while (queue.length > 0) {
    const next = queue.shift()!
    const kids = childrenByParent.get(next)
    if (!kids) continue
    for (const kid of kids) {
      if (seen.has(kid)) continue
      seen.add(kid)
      ids.push(kid)
      queue.push(kid)
    }
  }
  return ids
}

/**
 * Delete `nodeId` and every descendant under it. Returns the set of ids
 * that were deleted so the caller can react (e.g. clear selection if the
 * currently-selected node was inside the subtree).
 */
export async function deleteSchedulingNode(hierarchyId: string, nodeId: string): Promise<string[]> {
  const nodes = sessionSchedulingNodes.get(hierarchyId)
  if (!nodes) return []
  const deletedIds = collectSubtreeIds(nodes, nodeId)
  const toDelete = new Set(deletedIds)
  sessionSchedulingNodes.set(hierarchyId, nodes.filter(n => !toDelete.has(n.id)))
  if (isSupabaseMode()) {
    // Await the DB delete before returning so the caller's React Query refetch
    // reads the post-delete state (not a stale row set that races the delete).
    const { error } = await supabase.from('scheduling_nodes').delete().in('id', deletedIds)
    if (error) console.warn(`[hierarchy] Failed to delete scheduling_node subtree at ${nodeId}:`, error.message)
  }
  return deletedIds
}

// --- Financial Nodes ---

/** Financial nodes in canonical sibling order. See getSchedulingNodes. */
export async function getFinancialNodes(hierarchyId: string): Promise<FinancialHierarchyNode[]> {
  return sortHierarchyNodes(await loadFinancialNodes(hierarchyId))
}

async function loadFinancialNodes(hierarchyId: string): Promise<FinancialHierarchyNode[]> {
  // In Supabase mode, always query the database first. Otherwise an in-memory
  // session seed from generateDefaultHierarchies() shadows persisted nodes on
  // hard reload (the user-reported "import doesn't save after refresh" bug).
  if (isSupabaseMode()) {
    const rows = await querySupabaseJsonb<FinancialHierarchyNode>('financial_nodes', 'hierarchyId', hierarchyId)
    if (rows.length > 0) {
      sessionFinancialNodes.set(hierarchyId, rows)
      return rows
    }
  }

  if (sessionFinancialNodes.has(hierarchyId)) {
    return sessionFinancialNodes.get(hierarchyId)!
  }

  try {
    const data = await load<{ financialNodes: FinancialHierarchyNode[] }>('financial-nodes.json')
    const matches = data.financialNodes.filter(n => n.hierarchyId === hierarchyId)
    if (matches.length > 0) {
      sessionFinancialNodes.set(hierarchyId, matches)
      return matches
    }
  } catch {
    // Ignore
  }

  sessionFinancialNodes.set(hierarchyId, [])
  return []
}

export function addFinancialNode(node: FinancialHierarchyNode): FinancialHierarchyNode {
  const nodes = sessionFinancialNodes.get(node.hierarchyId) || []
  sessionFinancialNodes.set(node.hierarchyId, [...nodes, node])
  if (isSupabaseMode()) {
    void supabase.from('financial_nodes').upsert({ id: node.id, data: node }).then(({ error }) => {
      if (error) console.warn(`[hierarchy] Failed to persist financial_node ${node.id}:`, error.message)
    })
  }
  return node
}

/** Update a financial node in-place (creates new array reference for React Query) */
export async function updateFinancialNode(hierarchyId: string, nodeId: string, updates: Partial<FinancialHierarchyNode>): Promise<void> {
  const nodes = sessionFinancialNodes.get(hierarchyId)
  if (!nodes) return
  const merged = nodes.map(n => n.id === nodeId ? { ...n, ...updates } : n)
  sessionFinancialNodes.set(hierarchyId, merged)
  if (isSupabaseMode()) {
    const updatedNode = merged.find(n => n.id === nodeId)
    if (updatedNode) {
      const { error } = await supabase.from('financial_nodes').upsert({ id: nodeId, data: updatedNode })
      if (error) console.warn(`[hierarchy] Failed to persist financial_node update ${nodeId}:`, error.message)
    }
  }
}

export async function deleteFinancialNode(hierarchyId: string, nodeId: string): Promise<string[]> {
  const nodes = sessionFinancialNodes.get(hierarchyId)
  if (!nodes) return []
  const deletedIds = collectSubtreeIds(nodes, nodeId)
  const toDelete = new Set(deletedIds)
  sessionFinancialNodes.set(hierarchyId, nodes.filter(n => !toDelete.has(n.id)))
  if (isSupabaseMode()) {
    // Await the DB delete before returning so the caller's React Query refetch
    // reads the post-delete state (not a stale row set that races the delete).
    const { error } = await supabase.from('financial_nodes').delete().in('id', deletedIds)
    if (error) console.warn(`[hierarchy] Failed to delete financial_node subtree at ${nodeId}:`, error.message)
  }
  return deletedIds
}

// --- Cost Management Nodes ---

/** Cost nodes in canonical sibling order. See getSchedulingNodes. */
export async function getCostNodes(hierarchyId: string): Promise<CostHierarchyNode[]> {
  return sortHierarchyNodes(await loadCostNodes(hierarchyId))
}

async function loadCostNodes(hierarchyId: string): Promise<CostHierarchyNode[]> {
  // In Supabase mode, always query the database first so a stale session seed
  // from generateDefaultHierarchies() doesn't shadow persisted nodes.
  if (isSupabaseMode()) {
    const rows = await querySupabase<CostHierarchyNode>('cost_nodes', 'hierarchy_id', hierarchyId)
    if (rows.length > 0) {
      sessionCostNodes.set(hierarchyId, rows)
      return rows
    }
  }

  if (sessionCostNodes.has(hierarchyId)) {
    return sessionCostNodes.get(hierarchyId)!
  }

  try {
    const data = await load<{ costNodes: CostHierarchyNode[] }>('cost-nodes.json')
    const matches = data.costNodes.filter(n => n.hierarchyId === hierarchyId)
    if (matches.length > 0) {
      sessionCostNodes.set(hierarchyId, matches)
      return matches
    }
  } catch {
    // Ignore
  }

  sessionCostNodes.set(hierarchyId, [])
  return []
}

function costNodeToRow(node: CostHierarchyNode): Record<string, unknown> {
  return {
    id: node.id,
    hierarchy_id: node.hierarchyId,
    parent_node_id: node.parentNodeId,
    code: node.code,
    name: node.name,
    description: node.description,
    level: node.level,
    sort_order: node.sortOrder,
    node_type: node.nodeType,
    obs_node_id: node.obsNodeId,
    obs_node_name: node.obsNodeName,
    cam_user_id: node.camUserId,
    cam_user_name: node.camUserName,
    deliverables: node.deliverables ?? [],
    cost_burden_profile_id: node.costBurdenProfileId,
    cost_burden_profile_name: node.costBurdenProfileName,
    planned_value: node.plannedValue ?? 0,
    earned_value: node.earnedValue ?? 0,
    actual_cost: node.actualCost ?? 0,
    budget_at_completion: node.budgetAtCompletion ?? 0,
    estimate_at_completion: node.estimateAtCompletion ?? 0,
    planned_hours: node.plannedHours ?? 0,
    actual_hours: node.actualHours ?? 0,
    remaining_hours: node.remainingHours ?? 0,
    can_collect_actuals: node.canCollectActuals ?? false,
    financial_cost_collector_id: node.financialCostCollectorId,
    labor_categories: node.laborCategories ?? [],
  }
}

export function addCostNode(node: CostHierarchyNode): CostHierarchyNode {
  const nodes = sessionCostNodes.get(node.hierarchyId) || []
  sessionCostNodes.set(node.hierarchyId, [...nodes, node])
  if (isSupabaseMode()) {
    void supabase.from('cost_nodes').upsert(costNodeToRow(node)).then(({ error }) => {
      if (error) console.warn(`[hierarchy] Failed to persist cost_node ${node.id}:`, error.message)
    })
  }
  return node
}

export async function updateCostNode(hierarchyId: string, nodeId: string, updates: Partial<CostHierarchyNode>): Promise<void> {
  const nodes = sessionCostNodes.get(hierarchyId)
  if (!nodes) return
  const merged = nodes.map(n => n.id === nodeId ? { ...n, ...updates } : n)
  sessionCostNodes.set(hierarchyId, merged)
  if (isSupabaseMode()) {
    const updated = merged.find(n => n.id === nodeId)
    if (updated) {
      const { error } = await supabase.from('cost_nodes').upsert(costNodeToRow(updated))
      if (error) console.warn(`[hierarchy] Failed to persist cost_node update ${nodeId}:`, error.message)
    }
  }
}

export async function deleteCostNode(hierarchyId: string, nodeId: string): Promise<string[]> {
  const nodes = sessionCostNodes.get(hierarchyId)
  if (!nodes) return []
  const deletedIds = collectSubtreeIds(nodes, nodeId)
  const toDelete = new Set(deletedIds)
  sessionCostNodes.set(hierarchyId, nodes.filter(n => !toDelete.has(n.id)))
  if (isSupabaseMode()) {
    // Await the DB delete before returning so the caller's React Query refetch
    // reads the post-delete state (not a stale row set that races the delete).
    const { error } = await supabase.from('cost_nodes').delete().in('id', deletedIds)
    if (error) console.warn(`[hierarchy] Failed to delete cost_node subtree at ${nodeId}:`, error.message)
  }
  return deletedIds
}

// --- Cost Burden Profiles ---

export async function getCostBurdenProfiles(): Promise<CostBurdenProfile[]> {
  if (isSupabaseMode()) {
    const rows = await querySupabase<CostBurdenProfile>('cost_burden_profiles')
    if (rows.length > 0) return rows
  }
  const data = await load<{ costBurdenProfiles: CostBurdenProfile[] }>('cost-burden-profiles.json')
  return data.costBurdenProfiles
}

// --- OBS Hierarchies ---

export async function getObsHierarchies(projectId?: string): Promise<ObsHierarchy[]> {
  if (isSupabaseMode()) {
    const rows = await querySupabase<ObsHierarchy>('obs_hierarchies')
    if (rows.length > 0) {
      if (!projectId) return rows
      return rows.filter(h => h.scope === 'Enterprise' || h.projectId === projectId)
    }
  }
  const data = await load<{ obsHierarchies: ObsHierarchy[] }>('obs-hierarchies.json')
  if (!projectId) return data.obsHierarchies
  return data.obsHierarchies.filter(h => h.scope === 'Enterprise' || h.projectId === projectId)
}

// --- OBS Nodes ---

/** OBS nodes in canonical sibling order. See getSchedulingNodes. */
export async function getObsNodes(obsHierarchyId: string): Promise<ObsNode[]> {
  return sortHierarchyNodes(await loadObsNodes(obsHierarchyId))
}

async function loadObsNodes(obsHierarchyId: string): Promise<ObsNode[]> {
  // In Supabase mode, always query the database first so a stale session seed
  // doesn't shadow persisted nodes.
  if (isSupabaseMode()) {
    const rows = await querySupabase<ObsNode>('obs_nodes', 'obs_hierarchy_id', obsHierarchyId)
    if (rows.length > 0) {
      sessionObsNodes.set(obsHierarchyId, rows)
      return rows
    }
  }

  if (sessionObsNodes.has(obsHierarchyId)) {
    return sessionObsNodes.get(obsHierarchyId)!
  }

  try {
    const data = await load<{ obsNodes: ObsNode[] }>('obs-nodes.json')
    const matches = data.obsNodes.filter(n => n.obsHierarchyId === obsHierarchyId)
    if (matches.length > 0) {
      sessionObsNodes.set(obsHierarchyId, matches)
      return matches
    }
  } catch {
    // Ignore
  }

  sessionObsNodes.set(obsHierarchyId, [])
  return []
}

export function addObsNode(node: ObsNode): ObsNode {
  const nodes = sessionObsNodes.get(node.obsHierarchyId) || []
  sessionObsNodes.set(node.obsHierarchyId, [...nodes, node])
  return node
}

// --- Standard WBS Templates ---

const sessionCustomHierarchies = new Map<string, CustomWbsHierarchy[]>()
const sessionCustomNodes = new Map<string, CustomWbsNode[]>()

export async function getStandardWbsTemplates(): Promise<StandardWbsTemplate[]> {
  if (isSupabaseMode()) {
    const rows = await querySupabase<StandardWbsTemplate>('standard_wbs_templates')
    if (rows.length > 0) return rows
  }
  const res = await fetch(`${MOCK_BASE}/${HIERARCHY_DIR}/standard-wbs-templates.json`)
  if (!res.ok) throw new Error('Failed to load standard WBS templates')
  const data = await res.json() as { standardWbsTemplates: StandardWbsTemplate[] }
  return data.standardWbsTemplates
}

export async function getStandardWbsTemplate(templateId: string): Promise<StandardWbsTemplate | null> {
  const templates = await getStandardWbsTemplates()
  return templates.find(t => t.id === templateId) || null
}

export function getCustomHierarchies(projectId: string): CustomWbsHierarchy[] {
  return sessionCustomHierarchies.get(projectId) || []
}

export function getCustomNodes(customHierarchyId: string): CustomWbsNode[] {
  return sessionCustomNodes.get(customHierarchyId) || []
}

/** Create a custom hierarchy from a standard WBS template, importing all template elements as nodes */
export async function createHierarchyFromTemplate(
  projectId: string,
  templateId: string,
  projectName: string,
): Promise<CustomWbsHierarchy> {
  const template = await getStandardWbsTemplate(templateId)
  if (!template) throw new Error(`Template not found: ${templateId}`)

  const now = new Date().toISOString()
  const hierId = nextId('cwbs')

  const hierarchy: CustomWbsHierarchy = {
    id: hierId,
    projectId,
    name: `${projectName} — ${template.name}`,
    templateId: template.id,
    templateName: template.name,
    type: 'standard-wbs',
    status: 'Active',
    description: `Generated from ${template.standard} ${template.appendix}: ${template.description}`,
    createdDate: now,
    createdBy: 'user_pm1',
    lastModifiedDate: now,
    lastModifiedBy: 'user_pm1',
    nodeCount: template.elements.length,
  }

  // Convert template elements to custom WBS nodes.
  //
  // Mint every id first. Mapping and reading idMap in one forward pass left
  // any element whose parent is listed later in the template with a null
  // parent, and the repair loop that followed did `if (parentNodeId === null)
  // continue`, i.e. it skipped exactly the rows it existed to repair. With the
  // ids minted up front the parent always resolves and no repair pass is needed.
  const idMap = new Map<string, string>()
  for (const el of template.elements) idMap.set(el.id, nextId('cwn'))

  const nodes: CustomWbsNode[] = template.elements.map(el => {
    const nodeId = idMap.get(el.id)!
    return {
      id: nodeId,
      hierarchyId: hierId,
      customHierarchyId: hierId,
      parentNodeId: el.parentId ? (idMap.get(el.parentId) || null) : null,
      code: el.code,
      name: el.name,
      description: el.description,
      level: el.level,
      sortOrder: el.sortOrder,
      isFromTemplate: true,
      standardElementId: el.id,
      isRequired: el.isRequired,
      notes: null,
    }
  })

  const existing = sessionCustomHierarchies.get(projectId) || []
  sessionCustomHierarchies.set(projectId, [...existing, hierarchy])
  sessionCustomNodes.set(hierId, nodes)

  return hierarchy
}

export function addCustomNode(node: CustomWbsNode): CustomWbsNode {
  const nodes = sessionCustomNodes.get(node.customHierarchyId) || []
  sessionCustomNodes.set(node.customHierarchyId, [...nodes, node])
  return node
}

// --- Move / reorder nodes within a hierarchy ---

/** Move a node to a new parent and/or sort position. Works for any hierarchy type. */
/**
 * Recalculate descendant levels after a move.
 *
 * The visited set is load bearing. `walk` mutates `child.level` in place on the
 * very objects the session cache holds, so a parent/child cycle did not merely
 * blow the stack: it wrote thousands of ever-increasing level values into the
 * live cache on the way down, and the RangeError left them there.
 * assertMoveAllowed below stops a cycle being created in the first place; this
 * set stops a cycle already in the data from doing the same damage.
 */
function recalcLevels<T extends BaseHierarchyNode>(nodes: T[], movedId: string) {
  const byParent = new Map<string | null, T[]>()
  for (const n of nodes) {
    const key = n.parentNodeId ?? '__root__'
    const arr = byParent.get(key) || []
    arr.push(n)
    byParent.set(key, arr)
  }
  const visited = new Set<string>([movedId])
  function walk(parentId: string, parentLevel: number) {
    const children = byParent.get(parentId)
    if (!children) return
    for (const child of children) {
      if (visited.has(child.id)) continue
      visited.add(child.id)
      child.level = parentLevel + 1
      walk(child.id, child.level)
    }
  }
  const moved = nodes.find(n => n.id === movedId)
  if (moved) walk(movedId, moved.level)
}

/** Collect a node + all its descendants from a flat list. Cycle-safe. */
function subtreeOf<T extends BaseHierarchyNode>(nodes: T[], rootId: string): T[] {
  const byId = new Map(nodes.map(n => [n.id, n]))
  const childrenByParent = new Map<string, T[]>()
  for (const n of nodes) {
    if (!n.parentNodeId) continue
    const bucket = childrenByParent.get(n.parentNodeId)
    if (bucket) bucket.push(n)
    else childrenByParent.set(n.parentNodeId, [n])
  }
  const result: T[] = []
  const queue: string[] = [rootId]
  const seen = new Set<string>([rootId])
  while (queue.length) {
    const id = queue.shift()!
    const node = byId.get(id)
    if (!node) continue
    result.push(node)
    for (const child of childrenByParent.get(id) ?? []) {
      if (seen.has(child.id)) continue
      seen.add(child.id)
      queue.push(child.id)
    }
  }
  return result
}

/**
 * Refuse a move that would put a node inside its own subtree.
 *
 * The drag-and-drop path in HierarchyTree already blocks this with
 * isDescendant, but the WBS Update Helper path (handleAiMoveNode ->
 * move*Node) does not, and an unguarded cyclic reparent recurses in
 * recalcLevels until it throws RangeError, corrupting the session cache on the
 * way. Throwing here is deliberate: the AI apply loop catches per action and
 * reports the reason to the user.
 */
function assertMoveAllowed<T extends BaseHierarchyNode>(
  nodes: T[],
  nodeId: string,
  newParentId: string | null,
): void {
  if (newParentId === null) return
  if (newParentId === nodeId) {
    throw new Error('A node cannot be moved under itself.')
  }
  if (collectSubtreeIds(nodes, nodeId).includes(newParentId)) {
    const label = nodes.find(n => n.id === nodeId)?.code ?? nodeId
    throw new Error(`Cannot move ${label} under one of its own descendants: that would make the hierarchy a cycle.`)
  }
}

/** Apply a computed sibling reorder to a node array, returning the changed rows. */
function applySortOrders<T extends BaseHierarchyNode>(
  nodes: T[],
  changes: Array<{ id: string; sortOrder: number }>,
): { updated: T[]; changed: T[] } {
  const byId = new Map(changes.map(c => [c.id, c.sortOrder]))
  const changed: T[] = []
  const updated = nodes.map(n => {
    const next = byId.get(n.id)
    if (next === undefined || next === n.sortOrder) return n
    const clone = { ...n, sortOrder: next }
    changed.push(clone)
    return clone
  })
  return { updated, changed }
}

export async function moveSchedulingNode(hierarchyId: string, nodeId: string, newParentId: string | null, newSortOrder: number): Promise<void> {
  const nodes = sessionSchedulingNodes.get(hierarchyId)
  if (!nodes) return
  assertMoveAllowed(nodes, nodeId, newParentId)
  // Create new array with cloned node to trigger React re-render
  const updated = nodes.map(n => {
    if (n.id === nodeId) {
      const newLevel = newParentId ? (nodes.find(p => p.id === newParentId)?.level ?? 0) + 1 : 0
      return { ...n, parentNodeId: newParentId, sortOrder: newSortOrder, level: newLevel }
    }
    return n
  })
  recalcLevels(updated, nodeId)
  sessionSchedulingNodes.set(hierarchyId, updated)
  if (isSupabaseMode()) {
    const affected = subtreeOf(updated, nodeId)
    const { error } = await supabase.from('scheduling_nodes')
      .upsert(affected.map(n => ({ id: n.id, data: n })))
    if (error) console.warn(`[hierarchy] Failed to persist scheduling_node move ${nodeId}:`, error.message)
  }
}

export async function moveFinancialNode(hierarchyId: string, nodeId: string, newParentId: string | null, newSortOrder: number): Promise<void> {
  const nodes = sessionFinancialNodes.get(hierarchyId)
  if (!nodes) return
  assertMoveAllowed(nodes, nodeId, newParentId)
  const updated = nodes.map(n => {
    if (n.id === nodeId) {
      const newLevel = newParentId ? (nodes.find(p => p.id === newParentId)?.level ?? 0) + 1 : 0
      return { ...n, parentNodeId: newParentId, sortOrder: newSortOrder, level: newLevel }
    }
    return n
  })
  recalcLevels(updated, nodeId)
  sessionFinancialNodes.set(hierarchyId, updated)
  if (isSupabaseMode()) {
    const affected = subtreeOf(updated, nodeId)
    const { error } = await supabase.from('financial_nodes')
      .upsert(affected.map(n => ({ id: n.id, data: n })))
    if (error) console.warn(`[hierarchy] Failed to persist financial_node move ${nodeId}:`, error.message)
  }
}

export async function moveCostNode(hierarchyId: string, nodeId: string, newParentId: string | null, newSortOrder: number): Promise<void> {
  const nodes = sessionCostNodes.get(hierarchyId)
  if (!nodes) return
  assertMoveAllowed(nodes, nodeId, newParentId)
  const updated = nodes.map(n => {
    if (n.id === nodeId) {
      const newLevel = newParentId ? (nodes.find(p => p.id === newParentId)?.level ?? 0) + 1 : 0
      return { ...n, parentNodeId: newParentId, sortOrder: newSortOrder, level: newLevel }
    }
    return n
  })
  recalcLevels(updated, nodeId)
  sessionCostNodes.set(hierarchyId, updated)
  if (isSupabaseMode()) {
    const affected = subtreeOf(updated, nodeId)
    const { error } = await supabase.from('cost_nodes')
      .upsert(affected.map(costNodeToRow))
    if (error) console.warn(`[hierarchy] Failed to persist cost_node move ${nodeId}:`, error.message)
  }
}

export function moveObsNode(hierarchyId: string, nodeId: string, newParentId: string | null, newSortOrder: number) {
  const nodes = sessionObsNodes.get(hierarchyId)
  if (!nodes) return
  assertMoveAllowed(nodes, nodeId, newParentId)
  const updated = nodes.map(n => {
    if (n.id === nodeId) {
      const newLevel = newParentId ? (nodes.find(p => p.id === newParentId)?.level ?? 0) + 1 : 0
      return { ...n, parentNodeId: newParentId, sortOrder: newSortOrder, level: newLevel }
    }
    return n
  })
  recalcLevels(updated, nodeId)
  sessionObsNodes.set(hierarchyId, updated)
}

// --- Reorder a node among its siblings --------------------------------------
//
// Reordering is a sortOrder-only change: the parent and the levels do not move,
// so these deliberately skip recalcLevels and persist just the sibling group.
// Every function returns true when something actually moved, false at the end
// of the group, so the caller can skip the cache invalidation.

export async function reorderSchedulingNode(hierarchyId: string, nodeId: string, direction: 'up' | 'down'): Promise<boolean> {
  const nodes = sessionSchedulingNodes.get(hierarchyId)
  if (!nodes) return false
  const { updated, changed } = applySortOrders(nodes, computeSiblingReorder(nodes, nodeId, direction))
  if (changed.length === 0) return false
  sessionSchedulingNodes.set(hierarchyId, updated)
  if (isSupabaseMode()) {
    const { error } = await supabase.from('scheduling_nodes').upsert(changed.map(n => ({ id: n.id, data: n })))
    if (error) console.warn(`[hierarchy] Failed to persist scheduling_node reorder ${nodeId}:`, error.message)
  }
  return true
}

export async function reorderFinancialNode(hierarchyId: string, nodeId: string, direction: 'up' | 'down'): Promise<boolean> {
  const nodes = sessionFinancialNodes.get(hierarchyId)
  if (!nodes) return false
  const { updated, changed } = applySortOrders(nodes, computeSiblingReorder(nodes, nodeId, direction))
  if (changed.length === 0) return false
  sessionFinancialNodes.set(hierarchyId, updated)
  if (isSupabaseMode()) {
    const { error } = await supabase.from('financial_nodes').upsert(changed.map(n => ({ id: n.id, data: n })))
    if (error) console.warn(`[hierarchy] Failed to persist financial_node reorder ${nodeId}:`, error.message)
  }
  return true
}

export async function reorderCostNode(hierarchyId: string, nodeId: string, direction: 'up' | 'down'): Promise<boolean> {
  const nodes = sessionCostNodes.get(hierarchyId)
  if (!nodes) return false
  const { updated, changed } = applySortOrders(nodes, computeSiblingReorder(nodes, nodeId, direction))
  if (changed.length === 0) return false
  sessionCostNodes.set(hierarchyId, updated)
  if (isSupabaseMode()) {
    const { error } = await supabase.from('cost_nodes').upsert(changed.map(costNodeToRow))
    if (error) console.warn(`[hierarchy] Failed to persist cost_node reorder ${nodeId}:`, error.message)
  }
  return true
}

export function reorderObsNode(hierarchyId: string, nodeId: string, direction: 'up' | 'down'): boolean {
  const nodes = sessionObsNodes.get(hierarchyId)
  if (!nodes) return false
  const { updated, changed } = applySortOrders(nodes, computeSiblingReorder(nodes, nodeId, direction))
  if (changed.length === 0) return false
  sessionObsNodes.set(hierarchyId, updated)
  return true
}

// --- Import from other hierarchies into Execution WBS ---

export async function importNodesAsScheduling(
  sourceNodes: BaseHierarchyNode[],
  targetHierarchyId: string,
  parentNodeId: string | null,
): Promise<SchedulingHierarchyNode[]> {
  const idMap = new Map<string, string>()
  const imported: SchedulingHierarchyNode[] = []

  const sourceIds = new Set(sourceNodes.map(n => n.id))
  const existingNodes = sessionSchedulingNodes.get(targetHierarchyId) || []
  const parentLevel = parentNodeId
    ? (existingNodes.find(n => n.id === parentNodeId)?.level ?? 0)
    : -1
  const baseLevel = parentLevel + 1
  const sourceRoots = sourceNodes.filter(n => !n.parentNodeId || !sourceIds.has(n.parentNodeId))
  const minSourceLevel = sourceRoots.length > 0 ? Math.min(...sourceRoots.map(n => n.level)) : 0

  // Two passes over the source: mint every id BEFORE any parent is resolved.
  // Resolving inside the same forward loop meant a child listed ahead of its
  // parent found nothing in idMap and was silently re-parented onto the drop
  // target, while its level was still computed from its real depth.
  for (const src of sourceNodes) idMap.set(src.id, nextId('sn'))

  for (const src of sourceNodes) {
    const newId = idMap.get(src.id)!
    const isRoot = !src.parentNodeId || !sourceIds.has(src.parentNodeId)
    const newParent = isRoot ? parentNodeId : (idMap.get(src.parentNodeId!) || parentNodeId)
    const relativeLevel = src.level - minSourceLevel

    imported.push({
      id: newId,
      hierarchyId: targetHierarchyId,
      parentNodeId: newParent,
      code: src.code,
      name: src.name,
      description: src.description,
      level: baseLevel + relativeLevel,
      sortOrder: src.sortOrder,
      // Imported nodes always land as Summary — user can refine after import.
      elementType: 'Summary',
      schedulingWbsNodeId: null,
      responsibleOrg: null,
      budgetAtCompletion: null,
      earlyStart: null, earlyFinish: null, lateStart: null, lateFinish: null,
      actualStart: null, actualFinish: null, baselineStart: null, baselineFinish: null,
      percentComplete: null, totalFloat: null, activityCount: null,
    })
  }

  assertBulkResult('Execution WBS import', await bulkUpsertSchedulingNodes(targetHierarchyId, imported))
  return imported
}

// --- Import from other hierarchies into Financial WBS ---

export async function importNodesAsFinancial(
  sourceNodes: BaseHierarchyNode[],
  targetHierarchyId: string,
  parentNodeId: string | null,
  /**
   * Optional per-source-node attribute overrides applied at import time.
   * Keyed by the SOURCE node id (the row in the originating hierarchy);
   * values are partials of FinancialHierarchyNode whose fields override the
   * defaults that would otherwise be written. Used by the WBS Import
   * Mapping Chat to inject nodeType/billingType/etc. from a rule set the
   * user described in plain English.
   */
  overridesBySourceId?: Map<string, Partial<FinancialHierarchyNode>>,
): Promise<FinancialHierarchyNode[]> {
  const idMap = new Map<string, string>()
  const imported: FinancialHierarchyNode[] = []

  // Find root-level nodes in source (no parent, or parent not in selection)
  const sourceIds = new Set(sourceNodes.map(n => n.id))

  // Calculate the base level from the parent node
  const existingNodes = sessionFinancialNodes.get(targetHierarchyId) || []
  const parentLevel = parentNodeId
    ? (existingNodes.find(n => n.id === parentNodeId)?.level ?? 0)
    : -1
  const baseLevel = parentLevel + 1

  // Find the minimum level in source nodes to calculate relative offsets
  const sourceRoots = sourceNodes.filter(n => !n.parentNodeId || !sourceIds.has(n.parentNodeId))
  const minSourceLevel = sourceRoots.length > 0 ? Math.min(...sourceRoots.map(n => n.level)) : 0

  // Two passes over the source: mint every id BEFORE any parent is resolved.
  // Resolving inside the same forward loop meant a child listed ahead of its
  // parent found nothing in idMap and was silently re-parented onto the drop
  // target, while its level was still computed from its real depth.
  for (const src of sourceNodes) idMap.set(src.id, nextId('fn'))

  for (const src of sourceNodes) {
    const newId = idMap.get(src.id)!

    const isRoot = !src.parentNodeId || !sourceIds.has(src.parentNodeId)
    const newParent = isRoot ? parentNodeId : (idMap.get(src.parentNodeId!) || parentNodeId)
    const relativeLevel = src.level - minSourceLevel

    const override = overridesBySourceId?.get(src.id)
    imported.push({
      id: newId,
      hierarchyId: targetHierarchyId,
      parentNodeId: newParent,
      code: src.code,
      name: src.name,
      description: src.description,
      level: baseLevel + relativeLevel,
      sortOrder: src.sortOrder,
      nodeType: 'Summary',
      revenueRecognitionMethod: null,
      revRecTypeId: null,
      financialMapping: null,
      performanceObligationId: null,
      revRecId: null,
      contractId: null,
      billingType: null,
      periodOfPerformanceStart: null,
      periodOfPerformanceEnd: null,
      customerName: null,
      customerId: null,
      deliverableIds: [],
      costCollectorCode: null,
      allowActuals: false,
      budgetTotal: 0,
      budgetSpent: 0,
      budgetCommitted: 0,
      eacCost: null,
      eacBottomsUp: null,
      eacRiskAdjustment: null,
      eacFinanceAdjustment: null,
      eacVariableConsideration: null,
      ...override,
    })
  }

  // Persist via the shared bulk-upsert path so Supabase is updated, not just
  // the in-memory session cache.
  assertBulkResult('Financial WBS import', await bulkUpsertFinancialNodes(targetHierarchyId, imported))

  return imported
}

// --- Import from other hierarchies into Cost Management WBS ---

export async function importNodesAsCost(
  sourceNodes: BaseHierarchyNode[],
  targetHierarchyId: string,
  parentNodeId: string | null,
): Promise<CostHierarchyNode[]> {
  const idMap = new Map<string, string>()
  const imported: CostHierarchyNode[] = []

  const sourceIds = new Set(sourceNodes.map(n => n.id))

  // Calculate the base level from the parent node
  const existingCost = sessionCostNodes.get(targetHierarchyId) || []
  const parentCostLevel = parentNodeId
    ? (existingCost.find(n => n.id === parentNodeId)?.level ?? 0)
    : -1
  const baseCostLevel = parentCostLevel + 1

  const sourceRootsCost = sourceNodes.filter(n => !n.parentNodeId || !sourceIds.has(n.parentNodeId))
  const minSourceCostLevel = sourceRootsCost.length > 0 ? Math.min(...sourceRootsCost.map(n => n.level)) : 0

  // Two passes over the source: mint every id BEFORE any parent is resolved.
  // Resolving inside the same forward loop meant a child listed ahead of its
  // parent found nothing in idMap and was silently re-parented onto the drop
  // target, while its level was still computed from its real depth.
  for (const src of sourceNodes) idMap.set(src.id, nextId('cn'))

  for (const src of sourceNodes) {
    const newId = idMap.get(src.id)!

    const isRoot = !src.parentNodeId || !sourceIds.has(src.parentNodeId)
    const newParent = isRoot ? parentNodeId : (idMap.get(src.parentNodeId!) || parentNodeId)
    const relativeCostLevel = src.level - minSourceCostLevel

    imported.push({
      id: newId,
      hierarchyId: targetHierarchyId,
      parentNodeId: newParent,
      code: src.code,
      name: src.name,
      description: src.description,
      level: baseCostLevel + relativeCostLevel,
      sortOrder: src.sortOrder,
      nodeType: 'Summary',
      obsNodeId: null,
      obsNodeName: null,
      camUserId: null,
      camUserName: null,
      deliverables: [],
      costBurdenProfileId: null,
      costBurdenProfileName: null,
      plannedValue: 0,
      earnedValue: 0,
      actualCost: 0,
      budgetAtCompletion: 0,
      estimateAtCompletion: 0,
      plannedHours: 0,
      actualHours: 0,
      remainingHours: 0,
      canCollectActuals: false,
      financialCostCollectorId: null,
      laborCategories: [],
    })
  }

  assertBulkResult('Cost WBS import', await bulkUpsertCostNodes(targetHierarchyId, imported))

  return imported
}

// --- Import from Contract into Financial WBS ---

interface ContractImportData {
  contract: Record<string, unknown>
  taskOrders: Record<string, unknown>[]
  lineItems: Record<string, unknown>[]
}

export async function importContractAsFinancial(
  data: ContractImportData,
  targetHierarchyId: string,
  parentNodeId: string | null,
): Promise<FinancialHierarchyNode[]> {
  const { contract, taskOrders, lineItems } = data
  const contractType = (contract.contractType as string) || 'CPFF'
  const imported: FinancialHierarchyNode[] = []

  // Calculate base level from parent
  const existingFin = sessionFinancialNodes.get(targetHierarchyId) || []
  const parentFinLevel = parentNodeId
    ? (existingFin.find(n => n.id === parentNodeId)?.level ?? -1)
    : -1
  const level0 = parentFinLevel + 1

  // Determine RevRec method based on contract type
  const revRecMethod = contractType === 'FFP' ? 'POC-Output' as const
    : contractType === 'T&M' ? 'Point-in-Time' as const
    : 'POC-Input' as const // CPFF, CPIF, CPAF → cost-based

  const billingTypeMap: Record<string, BillingType> = {
    CPFF: 'CPFF', CPIF: 'CPIF', CPAF: 'CPAF', FFP: 'FFP', 'T&M': 'T&M',
  }

  // Helper to build a financial node template
  const makeFinNode = (overrides: Partial<FinancialHierarchyNode> & Pick<FinancialHierarchyNode, 'id' | 'parentNodeId' | 'code' | 'name' | 'description' | 'level' | 'sortOrder' | 'nodeType'>): FinancialHierarchyNode => ({
    hierarchyId: targetHierarchyId,
    revenueRecognitionMethod: null,
    revRecTypeId: null,
    financialMapping: null,
    performanceObligationId: null,
    revRecId: null,
    contractId: contract.id as string,
    billingType: null,
    periodOfPerformanceStart: null,
    periodOfPerformanceEnd: null,
    customerName: null,
    customerId: null,
    deliverableIds: [],
    costCollectorCode: null,
    allowActuals: false,
    budgetTotal: 0,
    budgetSpent: 0,
    budgetCommitted: 0,
    eacCost: 0,
    eacBottomsUp: 0,
    eacRiskAdjustment: 0,
    eacFinanceAdjustment: 0,
    eacVariableConsideration: 0,
    ...overrides,
  })

  // Separate task orders WITH and WITHOUT performance obligations
  const tosWithPO = taskOrders.filter(to => !!(to.performanceObligationId as string | null))
  const tosWithoutPO = taskOrders.filter(to => !(to.performanceObligationId as string | null))

  // Group TOs with POs by their performanceObligationId
  const poGroups = new Map<string, Record<string, unknown>[]>()
  for (const to of tosWithPO) {
    const poId = to.performanceObligationId as string
    if (!poGroups.has(poId)) poGroups.set(poId, [])
    poGroups.get(poId)!.push(to)
  }

  let globalSort = 0

  // --- Path A: Task orders WITH Performance Obligations ---
  // Structure: Revenue Element (PO) → CBW (task order) → Cost Collector (CLIN)
  for (const [poId, poTaskOrders] of poGroups) {
    globalSort++
    const revElementId = nextId('fn')
    const poTotalValue = poTaskOrders.reduce((s, to) => s + ((to.value as number) || 0), 0)

    imported.push(makeFinNode({
      id: revElementId,
      parentNodeId,
      code: poId,
      name: `Performance Obligation — ${poId}`,
      description: `Revenue element for performance obligation ${poId}`,
      level: level0,
      sortOrder: globalSort,
      nodeType: 'RevenueElement',
      revenueRecognitionMethod: revRecMethod,
      performanceObligationId: poId,
      financialMapping: {
        revenue: poTotalValue,
        billedRevenue: 0, unbilledRevenue: 0, wip: 0, cogs: 0,
        backlog: poTotalValue, remainingUnusedPerformanceObligation: poTotalValue,
      },
      customerName: (contract.contractorName as string) || null,
      customerId: (contract.contractorId as string) || null,
      periodOfPerformanceStart: (contract.startDate as string) || null,
      periodOfPerformanceEnd: (contract.endDate as string) || null,
      budgetTotal: poTotalValue,
      eacCost: poTotalValue,
      eacBottomsUp: poTotalValue,
    }))

    let cbwSort = 0
    for (const to of poTaskOrders) {
      cbwSort++
      const cbwId = nextId('fn')
      imported.push(makeFinNode({
        id: cbwId,
        parentNodeId: revElementId,
        code: (to.number as string) || `TO-${cbwSort}`,
        name: `Billing — ${to.title as string}`,
        description: `Customer billing WBS for task order ${to.number as string}`,
        level: level0 + 1,
        sortOrder: cbwSort,
        nodeType: 'CustomerBillingWbs',
        billingType: billingTypeMap[contractType] || 'CPFF',
        periodOfPerformanceStart: (to.startDate as string) || null,
        periodOfPerformanceEnd: (to.endDate as string) || null,
        customerName: (contract.contractorName as string) || null,
        customerId: (contract.contractorId as string) || null,
        budgetTotal: (to.value as number) || 0,
        eacCost: (to.value as number) || 0,
        eacBottomsUp: (to.value as number) || 0,
      }))

      // Cost Collectors for CLINs under this task order
      const toSalesOrders = (to.salesOrders as string[]) || []
      const toCLINs = lineItems.filter(li => toSalesOrders.includes(li.salesOrderId as string))
      let clinSort = 0
      for (const cli of toCLINs) {
        clinSort++
        imported.push(makeFinNode({
          id: nextId('fn'),
          parentNodeId: cbwId,
          code: `CC-${cli.clin as string}`,
          name: `Cost Collector — CLIN ${cli.clin as string}: ${cli.title as string}`,
          description: (cli.description as string) || '',
          level: level0 + 2,
          sortOrder: clinSort,
          nodeType: 'CostCollector',
          costCollectorCode: `CC-${(contract.number as string)?.slice(-5) || '00000'}-${cli.clin as string}`,
          allowActuals: true,
          periodOfPerformanceEnd: (cli.deliveryDate as string) || null,
          budgetTotal: (cli.value as number) || 0,
          eacCost: (cli.value as number) || 0,
          eacBottomsUp: (cli.value as number) || 0,
        }))
      }
    }
  }

  // --- Path B: Task orders WITHOUT Performance Obligations ---
  // The CBW itself IS the revenue element: nodeType = 'RevenueElement'
  // Structure: Revenue Element (task order as CBW/Rev combined) → Cost Collector (CLIN)
  for (const to of tosWithoutPO) {
    globalSort++
    const revCbwId = nextId('fn')

    imported.push(makeFinNode({
      id: revCbwId,
      parentNodeId,
      code: (to.number as string) || `TO-${globalSort}`,
      name: `${to.title as string}`,
      description: `Revenue element / billing WBS for task order ${to.number as string}`,
      level: level0,
      sortOrder: globalSort,
      nodeType: 'RevenueElement',
      revenueRecognitionMethod: revRecMethod,
      financialMapping: {
        revenue: (to.value as number) || 0,
        billedRevenue: 0, unbilledRevenue: 0, wip: 0, cogs: 0,
        backlog: (to.value as number) || 0,
        remainingUnusedPerformanceObligation: (to.value as number) || 0,
      },
      billingType: billingTypeMap[contractType] || 'CPFF',
      periodOfPerformanceStart: (to.startDate as string) || null,
      periodOfPerformanceEnd: (to.endDate as string) || null,
      customerName: (contract.contractorName as string) || null,
      customerId: (contract.contractorId as string) || null,
      budgetTotal: (to.value as number) || 0,
      eacCost: (to.value as number) || 0,
      eacBottomsUp: (to.value as number) || 0,
    }))

    // Cost Collectors for CLINs under this task order
    const toSalesOrders = (to.salesOrders as string[]) || []
    const toCLINs = lineItems.filter(li => toSalesOrders.includes(li.salesOrderId as string))
    let clinSort = 0
    for (const cli of toCLINs) {
      clinSort++
      imported.push(makeFinNode({
        id: nextId('fn'),
        parentNodeId: revCbwId,
        code: `CC-${cli.clin as string}`,
        name: `Cost Collector — CLIN ${cli.clin as string}: ${cli.title as string}`,
        description: (cli.description as string) || '',
        level: level0 + 1,
        sortOrder: clinSort,
        nodeType: 'CostCollector',
        costCollectorCode: `CC-${(contract.number as string)?.slice(-5) || '00000'}-${cli.clin as string}`,
        allowActuals: true,
        periodOfPerformanceEnd: (cli.deliveryDate as string) || null,
        budgetTotal: (cli.value as number) || 0,
        eacCost: (cli.value as number) || 0,
        eacBottomsUp: (cli.value as number) || 0,
      }))
    }
  }

  assertBulkResult('Financial WBS import', await bulkUpsertFinancialNodes(targetHierarchyId, imported))

  return imported
}

// --- Import from Contract into Cost Management WBS ---

export async function importContractAsCost(
  data: ContractImportData,
  targetHierarchyId: string,
  parentNodeId: string | null,
): Promise<CostHierarchyNode[]> {
  const { contract, taskOrders, lineItems } = data
  const imported: CostHierarchyNode[] = []

  // Calculate base level from parent
  const existingCostC = sessionCostNodes.get(targetHierarchyId) || []
  const parentCostCLevel = parentNodeId
    ? (existingCostC.find(n => n.id === parentNodeId)?.level ?? -1)
    : -1
  const costLevel0 = parentCostCLevel + 1

  // Create a Summary node for the contract
  const contractSummaryId = nextId('cn')
  imported.push({
    id: contractSummaryId,
    hierarchyId: targetHierarchyId,
    parentNodeId,
    code: (contract.number as string) || 'CTR',
    name: (contract.title as string) || 'Contract',
    description: `Cost structure for contract ${contract.number as string}`,
    level: costLevel0,
    sortOrder: 1,
    nodeType: 'Summary',
    obsNodeId: null,
    obsNodeName: null,
    camUserId: null,
    camUserName: null,
    deliverables: [],
    costBurdenProfileId: null,
    costBurdenProfileName: null,
    plannedValue: (contract.totalValue as number) || 0,
    earnedValue: 0,
    actualCost: 0,
    budgetAtCompletion: (contract.totalValue as number) || 0,
    estimateAtCompletion: (contract.totalValue as number) || 0,
    plannedHours: 0,
    actualHours: 0,
    remainingHours: 0,
    canCollectActuals: false,
    financialCostCollectorId: null,
    laborCategories: [],
  })

  // Create Control Accounts for each CLIN
  let sortOrder = 0
  for (const to of taskOrders) {
    const toLineItems = lineItems.filter(li => {
      const toSalesOrders = (to.salesOrders as string[]) || []
      return toSalesOrders.includes(li.salesOrderId as string)
    })

    for (const cli of toLineItems) {
      sortOrder++
      const caId = nextId('cn')
      imported.push({
        id: caId,
        hierarchyId: targetHierarchyId,
        parentNodeId: contractSummaryId,
        code: `CA-${cli.clin as string}`,
        name: `CA — CLIN ${cli.clin as string}: ${cli.title as string}`,
        description: (cli.description as string) || '',
        level: costLevel0 + 1,
        sortOrder,
        nodeType: 'ControlAccount',
        obsNodeId: null,
        obsNodeName: null,
        camUserId: null,
        camUserName: null,
        deliverables: [],
        costBurdenProfileId: null,
        costBurdenProfileName: null,
        plannedValue: (cli.value as number) || 0,
        earnedValue: 0,
        actualCost: 0,
        budgetAtCompletion: (cli.value as number) || 0,
        estimateAtCompletion: (cli.value as number) || 0,
        plannedHours: 0,
        actualHours: 0,
        remainingHours: 0,
        canCollectActuals: true,
        financialCostCollectorId: null,
        laborCategories: [],
      })
    }
  }

  assertBulkResult('Cost WBS import', await bulkUpsertCostNodes(targetHierarchyId, imported))

  return imported
}

// --- Hierarchy Node Mappings (Standard WBS ↔ Operational Hierarchies) ---

const sessionNodeMappings = new Map<string, HierarchyNodeMapping[]>()

/** Get all mappings for a given standard/custom hierarchy */
export function getNodeMappings(standardHierarchyId: string): HierarchyNodeMapping[] {
  return sessionNodeMappings.get(standardHierarchyId) || []
}

/** Add a mapping between a standard WBS node and an operational hierarchy node */
export function addNodeMapping(mapping: HierarchyNodeMapping): HierarchyNodeMapping {
  const existing = sessionNodeMappings.get(mapping.standardHierarchyId) || []
  sessionNodeMappings.set(mapping.standardHierarchyId, [...existing, mapping])
  return mapping
}

/** Remove a mapping by ID */
export function removeNodeMapping(standardHierarchyId: string, mappingId: string): void {
  const existing = sessionNodeMappings.get(standardHierarchyId) || []
  sessionNodeMappings.set(standardHierarchyId, existing.filter(m => m.id !== mappingId))
}

/** Get all mappings for a specific standard WBS node */
export function getNodeMappingsForNode(standardHierarchyId: string, standardNodeId: string): HierarchyNodeMapping[] {
  const all = sessionNodeMappings.get(standardHierarchyId) || []
  return all.filter(m => m.standardNodeId === standardNodeId)
}

// ============================================================
// Cross-Hierarchy Links
// ============================================================

const sessionCrossLinks = new Map<string, CrossHierarchyLink[]>()
let crossLinksLoaded = false
// Projects whose links have been read from the persistence layer at least once.
// After the first load the session map is authoritative: the add/remove writers
// keep it in sync and persist to Supabase fire-and-forget, so re-querying the DB
// on every refetch would transiently drop a just-added link whose upsert hasn't
// committed yet (the exact "linkages don't save" symptom).
const crossLinksLoadedProjects = new Set<string>()

export async function getCrossHierarchyLinks(projectId: string): Promise<CrossHierarchyLink[]> {
  if (crossLinksLoadedProjects.has(projectId)) {
    return sessionCrossLinks.get(projectId) || []
  }
  if (isSupabaseMode()) {
    // cross_hierarchy_links is a flat-JSONB table — filter on data->>projectId.
    // Supabase is the source of truth in this mode; reflect it as-is (an empty
    // result means the project genuinely has no links, not "fall back to mock").
    const rows = await querySupabaseJsonb<CrossHierarchyLink>('cross_hierarchy_links', 'projectId', projectId)
    sessionCrossLinks.set(projectId, rows)
    crossLinksLoadedProjects.add(projectId)
    return rows
  }
  if (!crossLinksLoaded) {
    try {
      const data = await load<{ crossHierarchyLinks: CrossHierarchyLink[] }>('cross-hierarchy-links.json')
      for (const link of data.crossHierarchyLinks) {
        const existing = sessionCrossLinks.get(link.projectId) || []
        existing.push(link)
        sessionCrossLinks.set(link.projectId, existing)
      }
    } catch { /* ignore */ }
    crossLinksLoaded = true
  }
  crossLinksLoadedProjects.add(projectId)
  return sessionCrossLinks.get(projectId) || []
}

export function addCrossHierarchyLink(
  link: Omit<CrossHierarchyLink, 'id'>,
): CrossHierarchyLink {
  const newLink: CrossHierarchyLink = { id: nextId('chl'), ...link }
  const existing = sessionCrossLinks.get(link.projectId) || []
  sessionCrossLinks.set(link.projectId, [...existing, newLink])
  if (isSupabaseMode()) {
    void supabase.from('cross_hierarchy_links').upsert({ id: newLink.id, data: newLink }).then(({ error }) => {
      if (error) console.warn(`[hierarchy] Failed to persist cross_hierarchy_link ${newLink.id}:`, error.message)
    })
  }
  return newLink
}

export function removeCrossHierarchyLink(projectId: string, linkId: string): void {
  const existing = sessionCrossLinks.get(projectId) || []
  sessionCrossLinks.set(projectId, existing.filter(l => l.id !== linkId))
  if (isSupabaseMode()) {
    void supabase.from('cross_hierarchy_links').delete().eq('id', linkId).then(({ error }) => {
      if (error) console.warn(`[hierarchy] Failed to delete cross_hierarchy_link ${linkId}:`, error.message)
    })
  }
}

// --- ID generator export ---

export { nextId }

// ---------------------------------------------------------------------------
// Copy-from-template: clone a source (template) project's WBS structure into
// the currently-selected project. Reuses the same id-remap + re-parent pattern
// as the in-project hierarchy import, but sources the nodes from ANOTHER
// project's hierarchies. Scheduling / financial / cost hierarchies are copied;
// each target default hierarchy is replaced with the cloned structure.
// ---------------------------------------------------------------------------

export interface WbsCopyResult {
  hierarchiesCopied: number
  nodesCopied: number
}

/**
 * Raise whatever a bulk upsert reported instead of dropping it.
 *
 * This used to discard the returned { failed, errors } and report success, so
 * a transient failure on chunk 2 orphaned the descendants in the later chunks
 * and the user was told the copy worked. The dialog that calls
 * copyWbsStructureFromProject catches and surfaces the message.
 */
function assertBulkResult(
  label: string,
  result: { inserted: number; failed: number; errors: string[] },
): void {
  if (result.failed === 0 && result.errors.length === 0) return
  throw new Error(
    `${label}: ${result.failed} of ${result.inserted + result.failed} nodes failed to copy. ${result.errors.join('; ')}`,
  )
}

/** Clone a node array into a target hierarchy with fresh ids + re-parenting. */
function cloneHierarchyNodes<T extends { id: string; hierarchyId: string; parentNodeId: string | null }>(
  src: T[],
  targetHierarchyId: string,
  idPrefix: string,
): T[] {
  const idMap = new Map<string, string>()
  for (const n of src) idMap.set(n.id, nextId(idPrefix))
  return src.map((n) => ({
    ...n,
    id: idMap.get(n.id)!,
    hierarchyId: targetHierarchyId,
    parentNodeId: n.parentNodeId ? (idMap.get(n.parentNodeId) ?? null) : null,
  }))
}

/**
 * Copy the WBS structure (scheduling / financial / cost hierarchies) from a
 * template project into a target project. The target's matching default
 * hierarchy is replaced with the cloned structure.
 */
export async function copyWbsStructureFromProject(
  sourceProjectId: string,
  targetProjectId: string,
  targetProjectName?: string,
  targetProjectCode?: string,
): Promise<WbsCopyResult> {
  const [srcHiers, tgtHiers] = await Promise.all([
    getWbsHierarchies(sourceProjectId),
    getWbsHierarchies(targetProjectId, targetProjectName, targetProjectCode),
  ])
  let hierarchiesCopied = 0
  let nodesCopied = 0

  const pickTarget = (type: WbsHierarchy['type']) =>
    tgtHiers.find((h) => h.type === type && h.isDefault) ?? tgtHiers.find((h) => h.type === type)

  // Scheduling
  const srcSched = srcHiers.find((h) => h.type === 'scheduling')
  const tgtSched = pickTarget('scheduling')
  if (srcSched && tgtSched) {
    const nodes = await getSchedulingNodes(srcSched.id)
    if (nodes.length > 0) {
      const cloned = cloneHierarchyNodes(nodes, tgtSched.id, 'sn')
      assertBulkResult('Execution WBS copy', await bulkUpsertSchedulingNodes(tgtSched.id, cloned, { replaceHierarchy: true }))
      hierarchiesCopied++
      nodesCopied += cloned.length
    }
  }

  // Financial
  const srcFin = srcHiers.find((h) => h.type === 'financial')
  const tgtFin = pickTarget('financial')
  if (srcFin && tgtFin) {
    const nodes = await getFinancialNodes(srcFin.id)
    if (nodes.length > 0) {
      const cloned = cloneHierarchyNodes(nodes, tgtFin.id, 'fn')
      assertBulkResult('Financial WBS copy', await bulkUpsertFinancialNodes(tgtFin.id, cloned, { replaceHierarchy: true }))
      hierarchiesCopied++
      nodesCopied += cloned.length
    }
  }

  // Cost
  const srcCost = srcHiers.find((h) => h.type === 'cost')
  const tgtCost = pickTarget('cost')
  if (srcCost && tgtCost) {
    const nodes = await getCostNodes(srcCost.id)
    if (nodes.length > 0) {
      const cloned = cloneHierarchyNodes(nodes, tgtCost.id, 'cn')
      assertBulkResult('Cost WBS copy', await bulkUpsertCostNodes(tgtCost.id, cloned, { replaceHierarchy: true }))
      hierarchiesCopied++
      nodesCopied += cloned.length
    }
  }

  return { hierarchiesCopied, nodesCopied }
}
